/**
 * Nierezydenci: podatek u źródła, certyfikat rezydencji i informacja IFT-1R
 * (Etap 48, ADR-0049).
 *
 * Art. 3 ust. 2a ustawy o PIT: osoba fizyczna bez miejsca zamieszkania
 * w Polsce podlega obowiązkowi podatkowemu tylko od dochodów osiąganych na
 * terytorium RP — to ograniczony obowiązek podatkowy.
 *
 * Art. 29 ust. 1 pkt 1: od przychodów z tytułów z art. 13 pkt 2 i 6–9 (a więc
 * z umowy zlecenia i z umowy o dzieło) pobiera się podatek **w formie ryczałtu
 * 20 % PRZYCHODU** — bez kosztów, bez kwoty zmniejszającej i bez odliczania
 * składek. Art. 29 ust. 2 pozwala zastosować stawkę z umowy o unikaniu
 * podwójnego opodatkowania albo nie pobrać podatku wcale, ale **tylko** gdy
 * miejsce zamieszkania jest udokumentowane **certyfikatem rezydencji**.
 *
 * Certyfikat jest zaświadczeniem organu podatkowego państwa zamieszkania.
 * Bez niego — albo poza okresem, który obejmuje — wraca stawka 20 %: i to jest
 * cała reguła tego modułu.
 *
 * O tym, czy ktoś jest rezydentem, system NIE orzeka: art. 3 ust. 1a wiąże to
 * z ośrodkiem interesów życiowych albo z 183 dniami pobytu, a obu tych faktów
 * lista płac nie zna. Rezydencja jest wpisem w aktach, tak jak PIT-2.
 */
import { nextBusinessDay } from './deadlines'
import type { ContractType } from './enums'

/** Rezydencja podatkowa jako fakt z akt (art. 3 ust. 1a i ust. 2a PIT). */
export const TAX_RESIDENCES = ['rezydent', 'nierezydent'] as const
export type TaxResidence = (typeof TAX_RESIDENCES)[number]

/** Art. 29 ust. 1 pkt 1 ustawy o PIT — ryczałt 20 % przychodu. */
export const NON_RESIDENT_TAX_BP = 2000

/**
 * Umowy, z których podatek pobiera się u źródła. Art. 29 ust. 1 pkt 1 odsyła
 * do art. 13 pkt 2 i 6–9, a punkt 8 obejmuje i zlecenie, i umowę o dzieło.
 * Stosunku pracy przepis NIE wymienia: wynagrodzenie nierezydenta z umowy
 * o pracę (art. 12) rozlicza się zaliczką według skali — art. 32, tak samo jak
 * u rezydenta.
 */
export function isWithholdingContract(type: ContractType): boolean {
  return type === 'zlecenie' || type === 'dzielo'
}

/** Czy certyfikat rezydencji obejmuje dzień wypłaty. */
export function isResidenceCertificateValid(input: {
  readonly from: string | null
  readonly to: string | null
  /** Dzień, dla którego sprawdzamy ważność — dzień wypłaty. */
  readonly on: string
}): boolean {
  if (input.from === null) return false
  if (input.on < input.from) return false
  /* Certyfikat bez daty końcowej jest bezterminowy tylko wtedy, gdy tak brzmi. */
  return input.to === null || input.on <= input.to
}

export interface NonResidentTaxInput {
  /** Stawka z umowy o unikaniu podwójnego opodatkowania; `null` = brak ustaleń. */
  readonly treatyTaxBp: number | null
  readonly certificate: { readonly from: string | null; readonly to: string | null }
  /** Dzień wypłaty — na niego sprawdzamy ważność certyfikatu. */
  readonly payoutDate: string
}

export interface NonResidentTaxResult {
  /** Stawka, którą wolno zastosować. */
  readonly taxBp: number
  /** Czy zastosowano stawkę z umowy (a nie ustawową). */
  readonly treatyApplied: boolean
  /**
   * Czy stawka z umowy była zadeklarowana, ale certyfikat jej nie potwierdza.
   * Wtedy liczymy 20 % i mówimy o tym wprost — art. 29 ust. 2 nie daje wyboru.
   */
  readonly certificateMissing: boolean
}

/**
 * Stawka podatku u źródła dla nierezydenta. Domyślnie 20 % z ustawy; stawka
 * z umowy o unikaniu podwójnego opodatkowania wchodzi tylko wtedy, gdy dzień
 * wypłaty mieści się w okresie ważności certyfikatu rezydencji.
 *
 * Stawka z umowy nie może być wyższa od ustawowej: umowa dzieli prawo do
 * opodatkowania między państwa, a nie podnosi krajowego podatku ponad 20 %.
 */
export function nonResidentTax(input: NonResidentTaxInput): NonResidentTaxResult {
  const valid = isResidenceCertificateValid({
    from: input.certificate.from,
    to: input.certificate.to,
    on: input.payoutDate,
  })
  if (input.treatyTaxBp === null) {
    return { taxBp: NON_RESIDENT_TAX_BP, treatyApplied: false, certificateMissing: false }
  }
  if (!valid) {
    return { taxBp: NON_RESIDENT_TAX_BP, treatyApplied: false, certificateMissing: true }
  }
  return {
    taxBp: Math.max(0, Math.min(NON_RESIDENT_TAX_BP, input.treatyTaxBp)),
    treatyApplied: true,
    certificateMissing: false,
  }
}

/**
 * Kod kraju rezydencji: dwie wielkie litery wg ISO 3166-1 alpha-2, tak jak
 * w polu „Kraj" formularza IFT-1R. Słownika państw nie wozimy — sprawdzamy
 * format, a poprawność kodu potwierdza człowiek, który go wpisuje.
 */
export function isCountryCode(value: string): boolean {
  return /^[A-Z]{2}$/.test(value)
}

/** Rezydencja i dane, których wymaga informacja IFT-1R. */
export interface NonResidentProfile {
  readonly taxResidence: TaxResidence
  /** Kod kraju rezydencji (ISO 3166-1 alpha-2); `null` = niewpisany. */
  readonly residenceCountry: string | null
  /** Zagraniczny numer identyfikacyjny podatnika; `null` = niewpisany. */
  readonly foreignTin: string | null
  readonly residenceCertificateFrom: string | null
  readonly residenceCertificateTo: string | null
  /** Stawka z umowy o unikaniu podwójnego opodatkowania w punktach bazowych. */
  readonly treatyTaxBp: number | null
}

/** Czego brakuje, żeby wystawić IFT-1R. Puste = komplet. */
export const IFT_ISSUE_KEYS = ['kraj', 'numer_zagraniczny'] as const
export type IftIssueKey = (typeof IFT_ISSUE_KEYS)[number]

/**
 * Braki nazwane pojedynczo, zamiast jednej odmowy „brakuje danych". Kod kraju
 * jest w formularzu obowiązkowy — bez niego informacja nie mówi, do którego
 * państwa trafia; zagraniczny numer identyfikacyjny bywa nieznany, więc jego
 * brak jest adnotacją, a nie przeszkodą.
 */
export function iftIssues(profile: NonResidentProfile): readonly IftIssueKey[] {
  const issues: IftIssueKey[] = []
  if (profile.residenceCountry === null || !isCountryCode(profile.residenceCountry)) {
    issues.push('kraj')
  }
  if (profile.foreignTin === null || profile.foreignTin.trim() === '') {
    issues.push('numer_zagraniczny')
  }
  return issues
}

/** Jeden miesiąc wypłaty w informacji IFT-1R. */
export interface Ift1rMonth {
  /** Miesiąc WYPŁATY (rrrr-mm) — przychód powstaje w dniu otrzymania. */
  readonly month: string
  readonly incomeGr: number
  readonly taxGr: number
  /** Stawka zastosowana w tym miesiącu (punkty bazowe). */
  readonly taxBp: number
}

/**
 * Informacja IFT-1R o przychodzie nierezydenta i pobranym podatku
 * (art. 42 ust. 2 pkt 2 ustawy o PIT). Wystawia się ją za rok, a na wniosek
 * podatnika także w trakcie roku — jako IFT-1 (art. 42 ust. 4).
 */
export interface Ift1rStatement {
  readonly employeeId: string
  readonly year: string
  readonly countryCode: string | null
  readonly foreignTin: string | null
  readonly months: readonly Ift1rMonth[]
  readonly incomeGr: number
  readonly taxGr: number
  /** Czy w którymkolwiek miesiącu zastosowano stawkę z umowy o UPO. */
  readonly treatyApplied: boolean
  readonly deadline: string
  /**
   * Dzień złożenia wniosku podatnika, gdy to IFT-1 z art. 42 ust. 4, a nie
   * roczny IFT-1R. Informacja obejmuje wtedy rok DO TEGO DNIA, a termin liczy
   * się od niego, nie od końca roku (Etap 52, ADR-0053).
   */
  readonly requestedOn?: string
}

/**
 * Termin przekazania IFT-1R: koniec lutego roku następnego (art. 42 ust. 2
 * pkt 2). Tak samo jak PIT-11 — ale IFT-1R idzie i do podatnika, i do urzędu
 * właściwego w sprawach opodatkowania osób zagranicznych.
 */
export function iftDeadline(year: number): string {
  /* Luty ma 29 dni w roku przestępnym — bierzemy „zerowy" dzień marca. */
  const lastOfFebruary = new Date(Date.UTC(year + 1, 2, 0)).toISOString().slice(0, 10)
  /*
   * Koniec lutego w sobotę albo niedzielę przechodzi na poniedziałek (art. 12
   * § 5 Ordynacji, #10). W lutym nie ma dnia ustawowo wolnego od pracy, więc
   * słownik świąt nie jest tu potrzebny.
   */
  return nextBusinessDay(lastOfFebruary, new Set())
}

/** Termin wystawienia IFT-1 na wniosek podatnika (art. 42 ust. 4 PIT). */
export const IFT_ON_REQUEST_DAYS = 14

/**
 * Termin sporządzenia i przesłania IFT-1: czternaście dni od dnia ZŁOŻENIA
 * wniosku (art. 42 ust. 4 PIT). Liczy się dzień złożenia, a nie dzień, w którym
 * ktoś wniosek odnotował — dlatego rejestr trzyma tę datę jako fakt.
 */
export function iftOnRequestDeadline(
  requestedOn: string,
  holidays: ReadonlySet<string> = new Set(),
): string {
  const day = new Date(`${requestedOn}T00:00:00.000Z`)
  day.setUTCDate(day.getUTCDate() + IFT_ON_REQUEST_DAYS)
  /* Termin z prawa podatkowego: z soboty i dnia wolnego na dzień roboczy (art. 12 § 5 Ordynacji). */
  return nextBusinessDay(day.toISOString().slice(0, 10), holidays)
}

/**
 * Stan wniosku o IFT-1 w rejestrze. „Po terminie" zostaje na zawsze — tak jak
 * przy zgłoszeniu RUD (ADR-0048): spóźnienia nie da się odwołać wystawieniem.
 */
export const IFT_REQUEST_STATES = ['do_wystawienia', 'wystawiona', 'po_terminie'] as const
export type IftRequestState = (typeof IFT_REQUEST_STATES)[number]

export function iftRequestState(
  row: { readonly requestedOn: string; readonly issuedOn: string | null },
  today: string,
  holidays: ReadonlySet<string> = new Set(),
): IftRequestState {
  const deadline = iftOnRequestDeadline(row.requestedOn, holidays)
  if (row.issuedOn === null) return today > deadline ? 'po_terminie' : 'do_wystawienia'
  return row.issuedOn > deadline ? 'po_terminie' : 'wystawiona'
}
